import React from 'react';

const Gemma4Launch = () => (
  <article className="news-article-body">
    <p className="news-lead">
      On April 2, 2026, Google launched Gemma 4 - an open model family that runs on a Raspberry Pi,
      a smartphone, or a single data-centre GPU. With an Apache 2.0 licence and four size options,
      it is Google's most accessible open AI release to date and a serious push into the edge-AI space.
    </p>

    <h2>Four models, one family</h2>
    <p>
      Gemma 4 ships in four variants designed for different hardware realities:
    </p>
    <ul>
      <li><strong>E2B and E4B (Edge models)</strong> - built for smartphones, Raspberry Pi, and NVIDIA Jetson Orin Nano.
        They run fully offline, process text, images, and audio natively, and maintain a 128K token context window.</li>
      <li><strong>26B Mixture-of-Experts (MoE)</strong> - the efficiency model. Despite 26 billion total parameters,
        it activates only <strong>3.8 billion parameters per token</strong> during inference - delivering
        performance comparable to a dense model several times larger, at the speed of a 4B model.</li>
      <li><strong>31B Dense</strong> - the flagship quality model. It supports a 256K token context window,
        fitting on a single 80 GB H100 GPU unquantized, or on a consumer RTX 4090 with 4-bit quantization.</li>
    </ul>

    <h2>What "quantization" means for edge deployment</h2>
    <p>
      Running a 31-billion-parameter model on consumer hardware sounds impossible - until you apply
      quantization. Instead of storing each model weight as a 16-bit or 32-bit floating-point number,
      4-bit quantization compresses weights to 4 bits each, cutting memory use by roughly 75–80%.
    </p>
    <p>
      The tradeoff is a small accuracy loss - but for most practical tasks, quantized Gemma 4 at 4-bit
      precision performs close enough to the full-precision version that it is deployable in production.
      This is what makes a 31B-parameter model fit on an RTX 4090 (24 GB VRAM), a card available
      to individual developers.
    </p>
    <p>
      The E2B and E4B edge models take this further: they are designed from the ground up to run
      on-device with no cloud dependency - important for privacy-sensitive applications and
      low-latency scenarios like real-time speech processing.
    </p>

    <h2>Multimodal from the start</h2>
    <p>
      All Gemma 4 models natively process text and images. The edge models add native audio input,
      enabling on-device speech recognition without a separate model. Images are handled through a
      configurable visual token budget (70 to 1,120 tokens) that adapts to image resolution,
      balancing quality and compute cost.
    </p>
    <p>
      The models are trained on more than 140 languages, making them broadly applicable across regions
      without fine-tuning.
    </p>

    <h2>Built for agentic workflows</h2>
    <p>
      Gemma 4 includes native support for function calling, structured JSON output, and system
      instructions. This is the baseline required for autonomous agent applications - where a model
      needs to call external tools, parse structured responses, and follow operator-defined rules.
    </p>
    <p>
      Day-one compatibility covers Hugging Face Transformers, vLLM, llama.cpp, MLX, Ollama,
      NVIDIA NIM, LM Studio, Google AI Studio, and Android Studio. Developers can run Gemma 4
      locally, deploy it via cloud APIs, or embed it in Android apps.
    </p>

    <h2>Apache 2.0 - and why it matters</h2>
    <p>
      Previous Gemma releases carried restrictive usage terms that limited commercial deployment.
      Gemma 4 ships under the Apache 2.0 licence - meaning organisations can use, modify, and
      distribute it freely for both personal and commercial purposes with no royalty obligations.
    </p>
    <p>
      This places Gemma 4 alongside Llama 4, Mistral Small 4, and Qwen 3.5 in the genuinely
      open tier of frontier models - and removes the legal hesitation that slowed enterprise
      adoption of earlier Gemma versions.
    </p>

    <div className="news-insight-box">
      <h3>Why this matters</h3>
      <p>
        Gemma 4 is not just another open model drop. The E2B and E4B edge variants push capable
        multimodal AI onto devices that previously could barely run a language model. The MoE 26B
        architecture demonstrates that intelligence-per-parameter is improving faster than raw
        parameter counts - a trend that will reshape hardware requirements for AI deployment.
        Combined with Apache 2.0 licensing, Gemma 4 is a genuine option for organisations that
        need frontier-quality AI without cloud dependency or licensing risk.
      </p>
    </div>
  </article>
);

export default Gemma4Launch;
